import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import env from './utils/env';

const ResetPassword: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (!token) {
            setError(t('auth.invalidResetToken'));
        }
    }, [token, t]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            if (!formData.password) {
                setError(t('auth.emailRequired'));
                setLoading(false);
                return;
            }

            if (formData.password !== formData.confirmPassword) {
                setError(t('auth.passwordsDoNotMatch'));
                setLoading(false);
                return;
            }

            // OWASP compatible password validation
            const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{12,}$/;
            if (!passwordRegex.test(formData.password)) {
                setError(t('auth.passwordPolicy'));
                setLoading(false);
                return;
            }

            const response = await fetch(`${env.apiUrl}/auth/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token,
                    password: formData.password
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || t('auth.invalidResetToken'));
            }

            setSuccess(true);
            setTimeout(() => {
                navigate('/');
            }, 3000);
        } catch (err: any) {
            setError(err.message || t('errors.anErrorOccurred'));
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="reset-password-container" style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '100vh',
                padding: '20px'
            }}>
                <div className="reset-password-card" style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '40px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    maxWidth: '400px',
                    width: '100%',
                    textAlign: 'center'
                }}>
                    <h2 style={{ color: '#dc3545', marginBottom: '20px' }}>{t('auth.invalidResetToken')}</h2>
                    <button 
                        onClick={() => navigate('/')}
                        style={{
                            backgroundColor: '#007bff',
                            color: 'white',
                            padding: '12px 24px',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '16px'
                        }}
                    >
                        {t('auth.backToLogin')}
                    </button>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="reset-password-container" style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '100vh',
                padding: '20px'
            }}>
                <div className="reset-password-card" style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '40px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    maxWidth: '400px',
                    width: '100%',
                    textAlign: 'center'
                }}>
                    <h2 style={{ color: '#28a745', marginBottom: '20px' }}>{t('auth.passwordResetSuccess')}</h2>
                    <p style={{ marginBottom: '20px' }}>You will be redirected to the login page in a few seconds.</p>
                    <button 
                        onClick={() => navigate('/')}
                        style={{
                            backgroundColor: '#007bff',
                            color: 'white',
                            padding: '12px 24px',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '16px'
                        }}
                    >
                        {t('auth.backToLogin')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="reset-password-container" style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '100vh',
            padding: '20px'
        }}>
            <div className="reset-password-card" style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '40px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                maxWidth: '400px',
                width: '100%'
            }}>
                <h2 style={{ textAlign: 'center', marginBottom: '30px' }}>{t('auth.resetPassword')}</h2>
                
                <form onSubmit={handleSubmit}>
                    <div className="form-group" style={{ marginBottom: '20px' }}>
                        <label htmlFor="password" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                            {t('auth.newPassword')}
                        </label>
                        <input
                            type="password"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder={t('auth.enterPassword')}
                            required
                            minLength={12}
                            autoComplete="new-password"
                            style={{
                                width: '100%',
                                padding: '12px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '16px',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>

                    <div className="form-group" style={{ marginBottom: '20px' }}>
                        <label htmlFor="confirmPassword" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                            {t('auth.confirmNewPassword')}
                        </label>
                        <input
                            type="password"
                            id="confirmPassword"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            placeholder={t('auth.confirmPasswordPlaceholder')}
                            required
                            minLength={12}
                            autoComplete="new-password"
                            style={{
                                width: '100%',
                                padding: '12px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '16px',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>

                    {error && (
                        <div className="error-message" style={{ 
                            color: '#dc3545', 
                            marginBottom: '20px', 
                            padding: '10px', 
                            backgroundColor: '#f8d7da', 
                            border: '1px solid #f5c6cb', 
                            borderRadius: '4px' 
                        }}>
                            {error}
                        </div>
                    )}

                    <button 
                        type="submit" 
                        disabled={loading}
                        style={{
                            width: '100%',
                            backgroundColor: loading ? '#6c757d' : '#007bff',
                            color: 'white',
                            padding: '12px',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontSize: '16px',
                            fontWeight: 'bold'
                        }}
                    >
                        {loading ? t('auth.processing') : t('auth.resetPassword')}
                    </button>
                </form>

                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <button 
                        onClick={() => navigate('/')}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#007bff',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        {t('auth.backToLogin')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;